import { DateRangePickerWithCollapsibleCalendar } from 'ui/datePicker/DateRangePickerWithCollapsibleCalendar';
import { PageHeader } from 'ui/pageHeader';
import { FlexSpacer } from 'ui/flexSpacer';
import { parseToDate } from 'utils/relativeDateTime';
import AddEditModal from './addEditModal';
import { AddWidgetButton } from './AddWidgetButton';
import DeleteModal from './deleteModal';
import MoreActionsDropdown from './moreActionsDropdown';
import SettingsModal from './settingsModal';
import DashboardGrid from './dashboardGrid';
import { DashboardPlaceholder } from './dashboardPlaceholder';
import {
  DashboardCSSWrapper,
  ToolbarContainer,
  ToolbarMockLabel,
  StyledSkeletonContainer,
  DateRangePickerWrapper,
} from './Dashboard.styled';

// type Props = {
//   /** Determines if add/edit modal is open. */
//   addEditModalOpen: boolean,
//   /** Function when an widget is added to dashboard. */
//   addWidget: ({}) => {},
//   /** Toggle add or edit mode on add/edit modal. */
//   addMode: boolean,
//   /** Function when default end range is changed. */
//   changeDefaultEndRange: (string) => {},
//   /** Function when default start range is changed. */
//   changeDefaultStartRange: (string) => {},
//   /** Function when end date is changed. */
//   changeEndDate: (moment) => {},
//   /** Function when end time is changed. */
//   changeEndTime: (moment) => {},
//   /** Function when start date is changed. */
//   changeStartDate: (moment) => {},
//   /** Function when start time is changed. */
//   changeStartTime: (moment) => {},
//   /** Function when theme is changed. */
//   changeTheme: (string) => {},
//   /** Function when add/edit modal is closed. */
//   closeAddEditModal: () => {},
//   /** Function when delete modal is closed. */
//   closeDeleteModal: () => {},
//   /** Function when settings modal is closed. */
//   closeSettingsModal: ({}) => {},
//   /** End time range of dashboard. */
//   defaultEndRange: string,
//   /** Start relative range of dashboard. */
//   defaultStartRange: moment,
//   /** Determines if delete modal is open. */
//   deleteModalOpen: boolean,
//   /** Function when an widget is deleted from dashboard. */
//   deleteWidget: (string) => {},
//   /** End date range of dashboard. */
//   endDate: moment,
//   /** End time range of dashboard. */
//   endTime: moment,
//   /** Function to fetch campaigns. */
//   fetchCampaigns: ({}) => void,
//   /** Function to fetch dashboard layout and metric/widget components (No Data). */
//   fetchDashboard: () => {},
//   /** Function to fetch dashboard data from each metric/widget. */
//   fetchDashboardData: () => {},
//   /** A flag to indicate if the dashboard layout is being fetched. */
//   fetchingDashboard: boolean,
//   /** A flag to indicate if the dashboard data is being fetched. */
//   fetchingData: boolean,
//   /** List of metrics */
//   highLevelMetrics: [],
//   /** If dashboard is in mock mode. */
//   isMockMode: boolean,
//   /** Array of dashboard layout panels needed by react-grid-layout. */
//   layout: array,
//   /** Function when a widget in the dashboard is moved or resized. */
//   layoutChange: ([]) => {},
//   /** If dashboard layout is loading */
//   layoutLoading: boolean,
//   /** Function when add/edit modal is open. */
//   openAddEditModal: ({}) => {},
//   /** Function when delete modal is open. */
//   openDeleteModal: (string) => {},
//   /** Function when settings modal is open. */
//   openSettingsModal: () => {},
//   /** Function to save dashboard layout and settings. */
//   saveDashboard: ({}) => {},
//   /** Function to save/update a dashboard widget. */
//   saveWidget: ({}) => {},
//   /** Currently selected widget in the add/edit modal. */
//   selectedWidget: {},
//   /** Callback function when dashboard changes. */
//   setDashboard: ([]) => {},
//   /** Determines if settings modal is open. */
//   settingsModalOpen: boolean,
//   /** Start date range of dashboard. */
//   startDate: moment,
//   /** Start time range of dashboard. */
//   startTime: string,
//   /** Dashboard Theme */
//   theme: string,
//   /** User context object. */
//   userContext: {},
//   /** Object of widgets in the dashboard. */
//   widgets: {},
// };

const Dashboard = (props) => {
  const {
    addEditModalOpen,
    addWidget,
    addMode,
    changeDefaultEndRange: handleChangeDefaultEndRange,
    changeDefaultStartRange: handleChangeDefaultStartRange,
    changeEndDate: handleChangeEndDate,
    changeStartDate: handleChangeStartDate,
    changeTheme,
    closeAddEditModal,
    closeDeleteModal,
    closeSettingsModal,
    defaultEndRange,
    defaultStartRange,
    deleteModalOpen,
    deleteWidget: handleDeleteWidget,
    endDate,
    fetchCampaigns,
    fetchingDashboard,
    fetchingData,
    fetchDashboardData,
    highLevelMetrics,
    isMockMode,
    layout,
    layoutChange,
    layoutLoading,
    openAddEditModal,
    openDeleteModal,
    openSettingsModal,
    saveDashboard,
    saveWidget,
    selectedWidget,
    setDashboard,
    settingsModalOpen,
    startDate,
    theme,
    userContext,
    widgets,
  } = props;

  /**
   * handleAddWidget
   * Callback function to add widget to dashboard.
   * @param { Object } widgetOptions: Data of new or edited widget.
   * @return { void }
   */
  const handleSaveWidget = (widgetOptions) => {
    if (addMode) {
      addWidget(widgetOptions);
    } else {
      saveWidget(widgetOptions);
    }
  };

  /**
   * isLayoutDifferent
   * Determine if layouts are different by filtering out moved attr.
   * @param { Array } newLayout: Array of updated layout to compare to current layout.
   * @returns { Boolean } If the layout is different from the current layout.
   */
  const isLayoutDifferent = (newLayout) => {
    const filterLayout = (layoutObject) => {
      const layoutString = JSON.stringify(layoutObject);
      const filterString = /,?("static":(true|false)|"moved":(true|false)),?/g;
      return layoutString.replace(filterString, '');
    };
    return filterLayout(layout) !== filterLayout(newLayout);
  };

  /**
   * handleLayoutChange
   * Callback function when the dashboard layout changes.
   * @param { Array } newLayout: The updated layout.
   * @return { void }
   */
  const handleLayoutChange = (newLayout) => {
    layoutChange(newLayout);

    // Checking length of 1 because adding a widget from a blank dashboard doesn't trigger a change.
    if (
      newLayout.length === 1 ||
      (isLayoutDifferent(newLayout) && !fetchingDashboard && !layoutLoading)
    ) {
      saveDashboard();
    }
  };

  /**
   * handleAddWidget
   * Callback function when add widget button is clicked.
   * @return { void }
   */
  const handleAddWidget = () => {
    openAddEditModal();
  };

  /**
   * handleEditWidget
   * Callback function when a dashboard widget is edited.
   * TODO Possibly pass chart config options when we have them.
   * @param { Object } widget: Dashboard widget to edit.
   * @return { void }
   */
  const handleEditWidget = (widget) => {
    openAddEditModal(widget);
  };

  /**
   * handleCloseDatePicker
   * Callback function when date picker is closed.
   * TODO Should do a fetch including the start and end time ranges.
   * @return { void }
   */
  const handleCloseDatePicker = () => {
    fetchDashboardData(widgets, startDate, endDate);
  };

  /**
   * handleSaveDashboardSettings
   * Callback function when settings are saved.
   * @param { Object } updatedSettings: Updated settings object (theme, startRange, endRange).
   * @return { void }
   */
  const handleSaveDashboardSettings = (updatedSettings) => {
    const { defaultStartRange: startRange, defaultEndRange: endRange } = updatedSettings;
    const updatedStartRange = parseToDate(startRange);
    const updateEndRange = parseToDate(endRange);

    /** Update Start and End date/times based on selected default date range. */
    handleChangeDefaultEndRange(endRange);
    handleChangeDefaultStartRange(startRange);
    handleChangeStartDate(updatedStartRange);
    handleChangeEndDate(updateEndRange);

    saveDashboard();

    // TODO: Limit fetch if start and end range has changed.
    fetchDashboardData(widgets, updatedStartRange, updateEndRange);
  };

  const numberOfWidgets = layout && layout.length;

  const toolbar = (
    <ToolbarContainer>
      {isMockMode && <ToolbarMockLabel>Dashboard Mock Mode</ToolbarMockLabel>}
      <DateRangePickerWrapper>
        <DateRangePickerWithCollapsibleCalendar
          startDate={startDate}
          endDate={endDate}
          onDateRangeChange={({ startDate: sd, endDate: ed }) => {
            handleChangeStartDate(sd);
            handleChangeEndDate(ed);
          }}
          horizontalAlignment="right"
          onClose={handleCloseDatePicker}
          blockFutureDates
          isTimeZonePickerDisabled
        />
      </DateRangePickerWrapper>
      <FlexSpacer>
        <AddWidgetButton numberOfWidgets={numberOfWidgets} onClick={handleAddWidget} />
        <MoreActionsDropdown openSettingsModal={openSettingsModal} />
      </FlexSpacer>
      {addEditModalOpen && (
        <AddEditModal
          addMode={addMode}
          fetchCampaigns={fetchCampaigns}
          metrics={highLevelMetrics}
          onSaveWidget={handleSaveWidget}
          onClose={closeAddEditModal}
          open={addEditModalOpen}
          selectedWidget={selectedWidget}
        />
      )}
      {deleteModalOpen && (
        <DeleteModal
          onClose={closeDeleteModal}
          onDelete={handleDeleteWidget}
          selectedWidget={selectedWidget}
        />
      )}
      <SettingsModal
        defaultEndRange={defaultEndRange}
        defaultStartRange={defaultStartRange}
        onClose={closeSettingsModal}
        onChangeTheme={changeTheme}
        onSetDashboard={setDashboard}
        onSave={handleSaveDashboardSettings}
        open={settingsModalOpen}
        theme={theme}
        userContext={userContext}
      />
    </ToolbarContainer>
  );

  return (
    <DashboardCSSWrapper
      data-fetching={fetchingDashboard}
      data-loading={layoutLoading}
      data-theme={theme}
    >
      <PageHeader pageActions={toolbar}>Dashboard</PageHeader>
      <StyledSkeletonContainer isLoading={fetchingDashboard}>
        {numberOfWidgets === 0 && <DashboardPlaceholder handleClick={handleAddWidget} />}
        <DashboardGrid
          isFetching={fetchingData}
          layout={layout}
          onLayoutChange={handleLayoutChange}
          onEditWidget={handleEditWidget}
          onDeleteWidget={openDeleteModal}
          theme={theme}
          widgets={widgets}
        />
      </StyledSkeletonContainer>
    </DashboardCSSWrapper>
  );
};

export default Dashboard;
